import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSiteData } from '../../context/DataContext';
import { motion, AnimatePresence } from 'framer-motion';
import { FiMenu, FiX } from 'react-icons/fi';

import './CategorySlider.css';

export default function CategorySlider() {
  const navigate = useNavigate();
  const { categories } = useSiteData();
  const [isPaused, setIsPaused] = useState(false);
  const [isListViewOpen, setIsListViewOpen] = useState(false);
  const scrollContainerRef = useRef(null);

  // Drag state
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeftState, setScrollLeftState] = useState(0);

  // Only duplicate if we have enough items for a marquee effect
  // If we have few items, we'll just show them normally
  const isSmallSet = categories.length <= 4;
  const duplicatedCategories = isSmallSet ? categories : [...categories, ...categories, ...categories];

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setStartX(e.pageX - scrollContainerRef.current.offsetLeft);
    setScrollLeftState(scrollContainerRef.current.scrollLeft);
    setIsPaused(true);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
    setIsPaused(false);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX - scrollContainerRef.current.offsetLeft;
    const walk = (x - startX) * 2; // Scroll speed
    scrollContainerRef.current.scrollLeft = scrollLeftState - walk;
  };

  const handleCategoryClick = (categoryId) => {
    if (isDragging) return;
    navigate(`/category/${categoryId}`);
    setIsListViewOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleViewAll = () => {
    if (window.innerWidth <= 768) {
      setIsListViewOpen(true);
    } else {
      navigate('/collections');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <div className="collection-section">
      <div className="collection-header">
        <h2 className="collection-title">Categories</h2>
        <button
          className="category-menu-btn"
          onClick={handleViewAll}
          aria-label="Toggle categories list"
        >
          <FiMenu />
          <span>View All</span>
        </button>
      </div>

      <div
        className="marquee-container"
        ref={scrollContainerRef}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onMouseEnter={() => setIsPaused(true)}
        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
      >
        <div 
          className={`marquee-content ${isPaused || isSmallSet ? 'paused' : ''}`}
          style={{ 
            justifyContent: isSmallSet ? 'center' : 'flex-start',
            width: isSmallSet ? '100%' : 'max-content' 
          }}
        >
          {duplicatedCategories.map((cat, idx) => (
            <div
              className="collection-item-card"
              key={`${cat.id}-${idx}`}
              onClick={() => handleCategoryClick(cat.id)}
            >
              <div className="collection-poster-box">
                <img src={cat.image} alt={cat.name} loading="lazy" />
              </div>
              <div className="collection-tag">
                <span>{cat.name}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <p className="drag-hint">Click and Drag to explore faster</p>

      {/* Mobile Modal List View */}
      <AnimatePresence>
        {isListViewOpen && (
          <>
            <motion.div
              className="modal-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsListViewOpen(false)}
            />
            <motion.div
              className="category-modal-card"
              initial={{ scale: 0.9, opacity: 0, x: '-50%', y: '-50%' }}
              animate={{ scale: 1, opacity: 1, x: '-50%', y: '-50%' }}
              exit={{ scale: 0.9, opacity: 0, x: '-50%', y: '-50%' }}
            >
              <div className="modal-header">
                <h3>Select Category</h3>
                <button onClick={() => setIsListViewOpen(false)}>
                  <FiX />
                </button>
              </div>
              <div className="modal-list">
                {categories.map((cat) => (
                  <div
                    key={cat.id}
                    className="modal-list-item"
                    onClick={() => handleCategoryClick(cat.id)}
                  >
                    <div className="modal-item-thumb">
                      <img src={cat.image} alt={cat.name} />
                    </div>
                    <span className="item-name">{cat.name}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
